/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import {
  ContactRestService,
  parsePredecessors,
  Resource,
  ScheduleTaskNoteListItem,
  ScheduleTaskType,
  scheduleTemplateNoteListItemDialog,
  ScheduleType,
  stringifyPredecessors,
  validatePredecessorsFormat,
} from '@gh/builder-shared-data';
import {
  CELL_FIRST_DRAGGABLE_COLUMN_CLASS,
  cellValueFormatter,
  code3Formatter,
  createDefaultGridModel,
  dictionaryValueFormatter,
  EntityInputDialogConfig,
  rendererIconByName,
  ROW_DELETED_CLASS,
  ROW_GROUP_CLASS,
  ROW_GROUP_DIRTY_CLASS,
  ROW_SELECTED_CLASS,
  ROW_TOP_CLASS,
} from '@gh/common-internal-shared';
import {
  DateTimeTypes,
  EMPTY_ENUM_DICTIONARY,
  EnumDictionary,
  LocalizationBusinessService,
  NumberTypes,
  State,
  TextTypes,
  whenModified,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DateFormat, FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { DialogService, yesAnswer, yesNoCancelConfirmation } from '@gh/core-ui';
import {
  buildButtonColumn,
  buildDateTimeColumn,
  buildEditableAutocompleteColumn,
  buildEditableCheckboxColumn,
  buildEditableColumn,
  buildEditableDateTimeColumn,
  buildEditableNumberColumn,
  buildEditableTextColumn,
  buildGroupColumn,
  CellParams,
  createCellModelFactory,
  EditableCellAccessor,
  EditableCellValue,
  FocusedCell,
  GridDefinition,
  GridDefinitionFactory,
  GridLayout,
} from '@gh/core-ui-grid';
import { coalesce, isNotNil, normalizeDate, regexpablePlainString } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { CellDoubleClickedEvent, ColDef, GridApi, RowDragEndEvent, ValueFormatterParams } from 'ag-grid-community';
import { find, isEmpty, isNil, map, trim } from 'lodash';
import { Observable } from 'rxjs';
import {
  GanttState,
  GanttStateEventType,
  queryGanttAllTasks,
  queryGanttChildLevel,
  queryGanttEditable,
  queryGanttFocusedCell,
  queryGanttIncludedTasksByPattern,
  queryGanttIsDateAllowed,
  queryGanttRootLevel,
  queryGanttSearchMatch,
  queryGanttShowCompletedTasks,
  queryGanttShowMarkedAsDeletedTasks,
  queryGanttTask,
  queryGanttTaskByTaskNo,
  queryGanttTasks,
  queryGanttTasksByParent,
  queryGanttVisibleTaskSeq,
} from '../../gantt-chart.state';
import {
  GanttTask,
  ganttTaskFieldState,
  ganttTaskRelationFromTask,
  ganttTaskRelationFromTaskDto,
  getGanttTaskId,
  isGanttStartTask,
  isGanttTaskEditable,
  isGanttTopTask,
  validateGanttTaskField,
} from '../../resources/gantt.model';
import { GanttChartBusService } from '../../services/gantt-chart-bus.service';
import { GanttPredecessorKitDialogConfig } from '../../services/gantt-chart-business.service';
import { GanttChartCommandService } from '../../services/gantt-chart-command.service';
import {
  GridPredecessorCellEditorComponent,
} from '../gantt-predecessor-cell-editor/gantt-predecessor-cell-editor.component';
import { ganttPredecessorKitDialogRoute } from '../gantt-predecessor-kit-dialog/gantt-predecessor-kit-dialog.component';
import { GanttChartRestService } from '../../services/gantt-chart-rest.service';

export const predecessorEditableValueFormatter = ({ value: { value } }: ValueFormatterParams) =>
  isEmpty(value) ? '' : stringifyPredecessors(value, 1);

@Component({
  selector: 'gh-gantt-chart-table',
  template: require('./gantt-chart-table.component.html'),
  styles: [require('./gantt-chart-table.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GanttChartTableComponent implements OnInit, OnChanges {
  @Input() gridLayout: GridLayout;
  @Input() taskTypeDict: EnumDictionary<ScheduleTaskType> = EMPTY_ENUM_DICTIONARY;
  @Input() nameEditable: boolean = true;
  @Input() showBaseDates: boolean = false;
  @Input() completionEditable: boolean = true;
  @Input() entityType: 'schedule' | 'template';
  @Input() scheduleType: ScheduleType;
  @Input() details: any ;
  @Input() saveBeforeSaveNote: any;

  @ContentChild('optionBar', { static: false }) optionBarTemplate?: TemplateRef<any>;
  selectedTaskId: ID;
  tasks$: Observable<GanttTask[]>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  gridDefinition: GridDefinition;

  get isNewSchedule(): boolean {
    if (this.entityType === 'schedule') {
      return !this.details.scheduleId;
    }
    return !this.details.templateId;
  }

  constructor(private state: State<GanttState>,
              private translations: TranslationService,
              private dialogs: DialogService,
              private ganttRestService: GanttChartRestService,
              private dialogService: DialogService,
              private localizationBusinessService: LocalizationBusinessService,
              private commands: GanttChartCommandService,
              private bus: GanttChartBusService,
              private contactService: ContactRestService,
              private changeDetect: ChangeDetectorRef,
              private gridDefinitionFactory: GridDefinitionFactory) {
    this.onEditorOpen = this.onEditorOpen.bind(this);
    this.getTreeLevel = this.getTreeLevel.bind(this);
  }

  ngOnInit(): void {
    this.gridDefinition = this.buildGridDefinition();
    this.tasks$ = combineLatest$([
      this.state.select(queryGanttTasks),
      this.state.select(queryGanttTasksByParent),
      this.state.select(queryGanttShowCompletedTasks),
      this.state.select(queryGanttShowMarkedAsDeletedTasks),
      this.bus.searchString$]).pipe(
      map$(() => {
        if (!!this.bus.searchString && this.bus.searchString.length) {
          const matches = {};
          const searchString = this.bus.searchString.toLowerCase();
          this.state.project(queryGanttAllTasks).forEach((task: GanttTask) => {
            if (task && task.taskId && this.state.project(queryGanttSearchMatch, task.taskId, searchString)) {
              matches[task.taskId] = true;
              let target = task;
              while (target.parentTaskId) {
                matches[target.parentTaskId] = true;
                target = this.state.value.tasks[target.parentTaskId];
              }
            }
          });
          return this.state.project(queryGanttVisibleTaskSeq).filter((task: GanttTask) => matches[task.taskId]);
        }
        return this.state.project(queryGanttVisibleTaskSeq);
      }));
    this.focusedCell$ = this.state.select(queryGanttFocusedCell);
    this.showBaseDates = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showBaseDates) {
      this.bus.setShowBaseDates(changes.showBaseDates.currentValue);
    }
  }

  onGridReady(api: GridApi): void {
    this.commands._setGridApi(api);
  }

  onFocusedCellChange(focusedCell: FocusedCell): void {
    this.commands.showTaskInTimeline(focusedCell.id);
    this.commands.resetFocusedCell();
  }

  getTreeLevel(task: Maybe<GanttTask>): Maybe<ID[]> {
    return task
      ? this.state.project(queryGanttChildLevel, getGanttTaskId(task))
      : this.state.project(queryGanttRootLevel);
  }
  sortRefField(a: any, b: any, row1: any, row2: any): number {
    const value1 = parseFloat([row1.data.costCenterCode, row1.data.orderNumber].filter(isNotNil).join('.'));
    const value2 = parseFloat([row2.data.costCenterCode, row2.data.orderNumber].filter(isNotNil).join('.'));
    if (!value1 && !!value2) {
      return -1;
    }
    if (!value1 && !value2) {
      return 0;
    }
    if (!!value1 && !value2) {
      return 1;
    }
    return value1 - value2;
  }

  onRowClick(event: any): void {
    this.commands.selectTask(event.data.taskId);
  }
  createTaskNote(text: string, task: GanttTask): void {
    if (this.entityType === 'schedule') {
      this.ganttRestService.updateCreateScheduleNote(
        this.scheduleType,
        this.details.scheduleId,
        task.physicalTaskId,
        { memo: text },
      ).subscribe((note) => this.commands.setTaskField(getGanttTaskId(task), 'noteMemo', note));
      return;
    }
    this.ganttRestService.updateCreateTemplateNote(
      this.details.templateId,
      task.physicalTaskId,
      { memo: text },
    ).subscribe((note) => this.commands.setTaskField(getGanttTaskId(task), 'noteMemo', note));
  }

  onCellDoubleClicked({ colDef, data }: CellDoubleClickedEvent): void {
    if (isGanttTaskEditable(data) && colDef.colId === 'status' && data.status === 0) {
      this.state.mutate({
                          type: GanttStateEventType.TaskSetField,
                          taskId: data.taskId,
                          field: 'status',
                          value: 1,
                        });
    }
  }

  private buildGridDefinition(): GridDefinition {
    const editable = this.state.project(queryGanttEditable);
    const model = createDefaultGridModel({
      editable,
      getId: getGanttTaskId,
      getCellDef: ganttTaskFieldState,
      validate: (task, name, value) => {
        switch (name) {
          case 'predecessors':
            return this.validatePredecessors(task, value);
          case 'startDate': {
            const errors = validateGanttTaskField(task, name, value);
            if (errors) {
              return errors;
            } else if (!this.state.project(queryGanttIsDateAllowed, value)) {
              return { startDate: { messageId: 'msg_schedule_incl_working_days' } };
            }
            break;
          }
          case 'endDate': {
            const errors = validateGanttTaskField(task, name, value);
            if (errors) {
              return errors;
            } else if (!this.state.project(queryGanttIsDateAllowed, value)) {
              return { endDate: { messageId: 'msg_schedule_incl_working_days' } };
            }
            break;
          }
          case 'noteMemo': {
            if (!trim(value).length) {
              return { format: { messageId: 'msg_field_invalid_format' }};
            }
            return validateGanttTaskField(task, name, value);
            break;
          }
          default:
            return validateGanttTaskField(task, name, value);
        }
      },
      command: (task, name, value) => {
        if (name === 'predecessors') {
          if (isNil(this.validatePredecessors(task, value))) {
            this.commands.setTaskField(
              getGanttTaskId(task),
              name,
              map(
                parsePredecessors(value),
                (predecessor) => {
                  let target;
                  this.state.project(queryGanttAllTasks).forEach((stateTask) => {
                    if (stateTask.taskNumber === predecessor.taskNo) {
                      target = this.bus.gantt.getTask(stateTask.taskId);
                    }
                  });
                  return ganttTaskRelationFromTaskDto(
                    target,
                    predecessor,
                    1,
                  );
                },
              ));
          }
        } else if (name === 'noteMemo') {
          if (!trim(value).length) {
            return;
          }
          if (this.entityType === 'schedule') {
            this.ganttRestService.updateCreateScheduleNote(
              this.scheduleType,
              this.details.scheduleId,
              task.physicalTaskId,
              {
                memo: trim(value),
                scheduleTaskNoteId: task.recentNote.scheduleTaskNoteId,
              }).subscribe((note) => {
              this.commands.setTaskField(getGanttTaskId(task), name, note);
            });
          } else {
            this.ganttRestService.updateCreateTemplateNote(
              this.details.templateId,
              task.physicalTaskId,
              {
                memo: trim(value),
                scheduleTaskNoteId: task.recentNote.scheduleTaskNoteId,
              }).subscribe((note) => {
              this.commands.setTaskField(getGanttTaskId(task), name, note);
            });
          }
        } else {
          this.commands.setTaskField(getGanttTaskId(task), name, value);
        }
      },
      getValue: (name, { value }) => name === 'predecessors' ? stringifyPredecessors(value, 1) : value,
    });

    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      model,
      layout: this.gridLayout,
      columns: {
        taskNumber: {
          rowDrag: ({ data }: { data: GanttTask }) => editable && !isGanttStartTask(data) && !data.markedAsDeleted,
          ...buildGroupColumn({
            hasChildrenProperty: 'hasChildren',
            isOpenedProperty: 'opened',
            onOpened: ({ node: { data }, open }) => this.commands.setTaskOpened(getGanttTaskId(data), open),
          }),
          valueFormatter: code3Formatter,
          cellClass: CELL_FIRST_DRAGGABLE_COLUMN_CLASS,
        },
        name: this.nameEditable ? buildEditableTextColumn(TextTypes.Text64, model.getCellAccessor('name')) : {},
        type: { valueFormatter: dictionaryValueFormatter(this.taskTypeDict) },
        ref: {
          cellRenderer: ({ data: { costCenterCode, orderNumber, jobOrderLink } }) => {
            const value = [costCenterCode, orderNumber].filter(isNotNil).join('/');
            if (jobOrderLink) {
              return `<a href="${jobOrderLink}" target="_blank">${value}</a>`;
            }
            return value;
          },
          comparator: this.sortRefField,
        },
        predecessors: this.buildEditablePredecessorColumn(model.getCellAccessor('predecessors')),
        duration: {
          ...buildEditableNumberColumn(NumberTypes.Duration, model.getCellAccessor('duration')),
          valueFormatter: cellValueFormatter<EditableCellValue>((value) =>
            isNil(value) || isNil(value.value) ? '' : this.localizationBusinessService.formatDaysDuration(value.value)),
        },
        completionPct: buildEditableNumberColumn(NumberTypes.Percents, model.getCellAccessor('completionPct')),
        donePct: {
          field: 'type',
          ...buildButtonColumn({
            type: 'raised',
            onClick: ({ data }) => this.toggleCompletion(data),
            isClickable: ({ data }) => editable && !data.hasChildren && !data.markedAsDeleted,
            label: this.translations.get('lbl_to_do_done'),
          }),
          cellClass: ({data}: {data: GanttTask}) => {
            const completion = coalesce(data.completionPct, 0);
            // tslint:disable-next-line:no-magic-numbers
            if (completion === 100) {
              return 'solid';
            } else if (completion > 0) {
              return 'tonal';
            }
            return 'outlined';
          },
        },
        startDate: {
          ...buildEditableDateTimeColumn(DateTimeTypes.ShortDate, model.getCellAccessor('startDate'), {
            format: DateFormat.Date,
            cursorOnFocus: 'start',
            isDateAllowed: (params, value) =>
              this.state.project(queryGanttIsDateAllowed, value),
          }),
          minWidth: 120,
        },
        endDate: {
          ...buildEditableDateTimeColumn(DateTimeTypes.ShortDate, model.getCellAccessor('endDate'), {
            format: DateFormat.Date,
            cursorOnFocus: 'start',
            isDateAllowed: (params, value) =>
              normalizeDate(params.data.startDate) <= value
              && this.state.project(queryGanttIsDateAllowed, value),
          }),
          minWidth: 120,
        },
        baseStartDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        baseEndDate: buildDateTimeColumn(DateTimeTypes.ShortDate),
        confirmedWith:  buildEditableTextColumn(TextTypes.Text64, model.getCellAccessor('confirmedWith')),
        confirmedOnDate: {
          ...buildEditableDateTimeColumn(DateTimeTypes.ShortDate, model.getCellAccessor('confirmedOnDate'), {
            format: DateFormat.Date,
            cursorOnFocus: 'start',
          }),
          minWidth: 120,
        },
        confirmedForDate: {
          ...buildEditableDateTimeColumn(DateTimeTypes.ShortDate, model.getCellAccessor('confirmedForDate'), {
            format: DateFormat.Date,
            cursorOnFocus: 'start',
          }),
          minWidth: 120,
        },
        resource: {
          ...buildEditableAutocompleteColumn(
            {
              nameProperty: ({ name }) => name,
              highlightedProperty: ({ isPreferred, isCcSupplier }) => !!isPreferred || !!isCcSupplier,
              loadMore: (event, params) => event.load(this.loadSuppliers(event.query, params.data.costCenterId)),
            },
            model.getCellAccessor('resource')),
          cellClassRules: {
            'warn-color': ({ data: { isResourceInUse } }: { data: GanttTask }) => isResourceInUse,
          },
        },
        notes: {
          ...buildEditableTextColumn(
            TextTypes.Text64,
            model.getCellAccessor('noteMemo'),
            {
              menu: [
                {
                  onClick: (task) => {
                    if (!task.physicalTaskId) {
                      const confirmation$ = this.dialogs.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));
                      confirmation$.pipe(
                        filter$(yesAnswer),
                        switchMap$(() => this.saveBeforeSaveNote().toFinalState()),
                      ).subscribe(
                        ({ value }: any) => {
                          this.dialogService.openWithInput(
                                scheduleTemplateNoteListItemDialog,
                                <EntityInputDialogConfig<ScheduleTaskNoteListItem>>{
                                  titleId: 'lbl_add_note',
                                  placeholderId: 'lbl_note',
                                  saveButtonLabelId: 'lbl_add',
                                  field: 'memo',
                                  required: true,
                                  showTextArea: true,
                                  minHeight: 300,
                                })
                              .afterClosed().pipe(
                                filter$(Boolean))
                              .subscribe((newNote: ScheduleTaskNoteListItem) => {
                                const target = value.tasks.find((item: any) => task.taskNumber === item.taskNumber);
                                this.createTaskNote(newNote.memo || '', {
                                  ...task,
                                  physicalTaskId: target ? target.physicalTaskId : task.physicalTaskId,
                                });
                                },
                              );
                        },
                      );
                      return;
                    }
                    this.dialogService.openWithInput(
                          scheduleTemplateNoteListItemDialog,
                          <EntityInputDialogConfig<ScheduleTaskNoteListItem>>{
                            titleId: 'lbl_add_note',
                            placeholderId: 'lbl_note',
                            saveButtonLabelId: 'lbl_add',
                            field: 'memo',
                            required: true,
                            showTextArea: true,
                            minHeight: 300,
                          })
                        .afterClosed().pipe(
                          filter$(Boolean))
                        .subscribe((newNote: ScheduleTaskNoteListItem) => this.createTaskNote(newNote.memo || '', task),
                        );
                  },
                  text: this.translations.get('lbl_add_new_note'),
                  icon: 'gh:playlist-add',
                },
              ],
              showMenu: (data) => !this.isNewSchedule && !isGanttStartTask(data) && !data.markedAsDeleted,
            },
          ),
          comparator: (a, b) => {
            if (!a.value && !!b.value) {
              return -1;
            } else if (!!a.value && !b.value) {
              return 1;
            } else if (!!a.value && !!b.value) {
              return a.value.localeCompare(b.value);
            }
            return 0;
          },
          editable: ({ data }) => data.noteMemo && !this.isNewSchedule && !isGanttStartTask(data),
        },
        isExcludeFromGanttChart: buildEditableCheckboxColumn(model.getCellAccessor('isExcludeFromGanttChart')),
        description: buildEditableTextColumn(TextTypes.Text64, model.getCellAccessor('description')),
        status: {
          field: 'status',
          cellRenderer: ({ data: { status } }) =>
            status === 0 ? rendererIconByName('warning-var', '#ff9e21') : '',
        },
      },
      options: <any>{
        defaultColDef: {
          sortable: false,
        },
        enableGroupEdit: true,
        onRowDragEnd: ({ node, overNode }: RowDragEndEvent) =>
          this.commands.dragTask(node.data, overNode && overNode.data),
        rowClassRules: {
          [ROW_GROUP_CLASS]: ({ data: { hasChildren } }: { data: GanttTask }) => hasChildren,
          [ROW_GROUP_DIRTY_CLASS]: ({ data }: { data: GanttTask }) => whenModified(data),
          [ROW_TOP_CLASS]: ({ data }: { data: GanttTask }) => isGanttTopTask(data),
          [ROW_SELECTED_CLASS]: ({ data }: { data: GanttTask }) => !!data.selected,
          [ROW_DELETED_CLASS]: ({ data: { markedAsDeleted } }: { data: GanttTask }) => markedAsDeleted,
        },
      },
    });
  }

  private toggleCompletion(task: GanttTask): void {
    const currentCompletionPct = coalesce(task.completionPct, 0);
    // tslint:disable-next-line:no-magic-numbers
    const half = 50;
    const done = 100;
    let nextCompletionPct;
    if (currentCompletionPct === half) {
      nextCompletionPct = 0;
    } else if (currentCompletionPct === done) {
      nextCompletionPct = half;
    } else {
      nextCompletionPct = done;
    }
    this.commands.setTaskField(getGanttTaskId(task), 'completionPct', nextCompletionPct);
  }

  private onEditorOpen(params: CellParams): void {
    const { data: { taskId } } = params;
    const task = this.state.project(queryGanttTask, taskId);
    this.dialogs.openWithInput(ganttPredecessorKitDialogRoute, <GanttPredecessorKitDialogConfig>{
      taskId,
      name: task.name,
      predecessors: task.predecessors,
      validateRelations: (sourceTaskId, relations) =>
        this.commands.validateRelations(sourceTaskId, relations),
      findTasks: (scheduleId, sourceTaskNumber, excluding) =>
        this.state.project(
          queryGanttIncludedTasksByPattern,
          scheduleId,
          new RegExp(regexpablePlainString(sourceTaskNumber), 'i'),
          excluding)
          .map(({ taskId: foundTaskId, taskNumber: foundTaskNumber, name }) => ({
            id: foundTaskId,
            name: String(foundTaskNumber),
            display: `${foundTaskNumber} ${name}`,
          })),
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((relations) => this.commands.setTaskField(taskId, 'predecessors', relations));
  }

  private buildEditablePredecessorColumn(accessor: EditableCellAccessor): Partial<ColDef> {
    const modelFactory = createCellModelFactory(accessor);

    return {
      ...buildEditableColumn(accessor, {
        modelFactory,
        align: 'left',
        editorTrigger: {
          iconName: 'gh:functions',
          tooltip: this.translations.get('lbl_add_predecessors'),
        },
        onEditorOpen: this.onEditorOpen,
      }),
      cellEditorFramework: GridPredecessorCellEditorComponent,
      cellEditorParams: {
        modelFactory,
        align: 'left',
        onEditorOpen: this.onEditorOpen,
      },
      valueFormatter: predecessorEditableValueFormatter,
    };
  }
  private loadSuppliers(query: FilteredDataQuery<string>, costCenterId: ID | null = null): Observable<any[]> {
    return this.contactService.findAllResourcesAsDataSource({
                                                              searchString: query.filter,
                                                              costCenterId,
                                                            }).select(query).pipe(
      map$(getSlicedData),
      map$((items: Resource[]) => items.map((item) => ({
        name: item.name,
        id: item.contactId,
        isPreferred: item.isPreferred,
        isCcSupplier: item.isCcSupplier,
        isEmployee: item.isEmployee,
      }))));
  }
  private validatePredecessors(task: GanttTask, expression: string): Maybe<ValidationErrors> {
    if (!validatePredecessorsFormat(expression)) {
      return { format: { messageId: 'msg_pred_invalid_format' } };
    }

    const relations = parsePredecessors(expression);
    if (isNil(relations)) {
      return;
    }
    let twoRelationsToSameTask: boolean = false;
    const relationsTaskNumber: number[] = [];
    relations.forEach((relation) => {
      if (relationsTaskNumber.includes(relation.taskNo)) {
        twoRelationsToSameTask = true;
      } else {
        relationsTaskNumber.push(relation.taskNo);
      }
    });
    // if (twoRelationsToSameTask) {
    //   return { predecessorInvalidRelation: { messageId: 'msg_sch_cyclic_dependence' } };
    // }
    const notExistingTask = find(
      relations,
      ({ taskNo }) => {
        const target = this.state.project(queryGanttTaskByTaskNo, taskNo);
        return target ? !this.commands.isIncludedTaskExist(target.taskId) : true;
      },
    );
    if (notExistingTask) {
      return { predecessorNotExistingTask: { messageId: 'msg_pred_invalid_format' } };
    }

    const isRelationsAllowed = this.commands.isRelationsAllowed(
      getGanttTaskId(task),
      relations.map((rel) => ganttTaskRelationFromTask(this.state.project(queryGanttAllTasks), rel)));
    if (!isRelationsAllowed) {
      return { predecessorInvalidRelation: { messageId: 'msg_sch_cyclic_dependence' } };
    }
    return;
  }
}
